import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';

import { ATTACHMENT_COLORS } from '../../constants/attachmentColors';
import { attachmentService } from '../../services/attachmentService';
import {
  isImageMimeType,
  isVideoMimeType,
} from '../../services/mediaViewerService';
import { type AttachmentThumbnailProps } from '../../types/attachmentInterfaces';
import { getAttachmentIcon, isPDFType } from '../../utils/attachmentHelpers';
import { Play } from '../icons';

/**
 * AttachmentThumbnail component
 * Displays thumbnail for images/videos or appropriate icon for other file types.
 * Implements lazy loading on web using Intersection Observer.
 *
 * ⚠️ TWO FORMS, AND THE SECOND ONE'S WHOLE PURPOSE IS THAT IT DOES NOT FETCH
 * (#1069 Story 8.5, D1a). Given `attachment` it behaves exactly as it always
 * has: observe, then ask `attachmentService` for a thumbnail, then fall back to
 * the full file. Given `source` the caller already holds the picture, so every
 * one of those steps is skipped — see the bail at the top of both effects. A
 * correspondence file row uses the second form; the four request-side callers
 * (`AttachmentListItem`, `SummaryAttachmentRow`, `SummaryImageRef`,
 * `ImageSelectionModal`) use the first and are unchanged.
 */
export const AttachmentThumbnail: React.FC<
  AttachmentThumbnailProps
> = props => {
  const {
    size,
    onPress,
    error = false,
    loading: externalLoading = false,
    containerStyle,
  } = props;
  // Read off the union rather than destructured, so neither branch's absent
  // key has to be faked. Exactly one is present; the type guarantees it.
  const source = props.source;
  const attachment = props.attachment;
  /**
   * ⚠️ A BOOLEAN, NOT `source` ITSELF, BECAUSE IT IS AN EFFECT DEPENDENCY.
   * Callers build `source` as an object literal, so a new identity arrives on
   * every render; depending on the object would re-run the observer effect
   * each time. What the effects actually need to know is the one bit.
   */
  const hasSource = source !== undefined;

  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(error);
  const [isVisible, setIsVisible] = useState(Platform.OS !== 'web'); // Native always visible
  // The `source.uri` whose image failed to decode or load, if any. Keyed by
  // the uri rather than a boolean so a NEW uri is tried afresh with no reset
  // effect (#1069 MR !597 review).
  const [failedSourceUri, setFailedSourceUri] = useState<string | null>(null);
  const containerRef = useRef<View>(null);

  // Determine size in pixels based on size prop
  // 40dp renders as 80px @2x, 120px @3x (xsmall - card layout)
  // 48dp renders as 96px @2x, 144px @3x (small - legacy)
  // 56dp renders as 112px @2x, 168px @3x (large - table layout)
  const sizeInDp = size === 'xsmall' ? 40 : size === 'small' ? 48 : 56;

  const mimeType = source ? source.mimeType : attachment?.mimeType;
  const isImage = isImageMimeType(mimeType || '');
  const isVideo = isVideoMimeType(mimeType || '');
  const shouldShowThumbnail = isImage || isVideo;

  // Intersection Observer for lazy loading (web only)
  useEffect(() => {
    // D1a: a `source` is already in hand, so there is nothing to become
    // visible FOR. Bails before the observer is constructed, not after, which
    // is what Story 8.5's AC 3 pins.
    if (hasSource) {
      return;
    }
    if (Platform.OS !== 'web' || !containerRef.current) {
      return;
    }

    const element = containerRef.current as unknown as Element;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [hasSource]);

  // Load thumbnail when visible
  useEffect(() => {
    // D1a: the caller handed us the picture (or told us there is none). No
    // service call, and the full-file fallback below is never entered — that
    // fallback is the expensive one, and on a correspondence file id it would
    // be a request-attachments lookup for a row that does not exist.
    //
    // ⚠️ `hasSource` IS REDUNDANT WHILE THE UNION HOLDS, AND IS KEPT ON
    // PURPOSE (measured 2026-09-21). There is no `attachment` on the `source`
    // form, so `!attachment` alone already bails — deleting `hasSource` here
    // does NOT by itself stop the correspondence row working, and the record
    // says so rather than claiming a proof it does not have. What it buys is
    // PRECEDENCE, which IS observable and IS pinned: handed both (an untyped
    // caller, the only way to get there), `source` wins and nothing is
    // fetched. It also states the rule at the top of the effect rather than
    // leaving the reader to infer it from an absent field.
    if (hasSource || !attachment) {
      return;
    }
    if (!isVisible || !shouldShowThumbnail) {
      return;
    }

    const loadThumbnail = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        // Check if video has embedded thumbnail
        if (isVideo && attachment.thumbnail) {
          setThumbnailUrl(
            `data:${attachment.thumbnailMimeType || 'image/jpeg'};base64,${
              attachment.thumbnail
            }`,
          );
          setIsLoading(false);
          return;
        }

        // For videos without embedded thumbnail, try to get from service
        // For images, always try to get thumbnail
        const url = await attachmentService.getAttachmentThumbnail(
          attachment.id,
        );
        if (url) {
          setThumbnailUrl(url);
        } else if (isImage) {
          // Fallback for images only: Load full file for legacy attachments without thumbnails
          console.log(
            `📥 No thumbnail for image attachment ${attachment.id}, loading full file as fallback`,
          );
          const result = await attachmentService.getAttachment(attachment.id);
          if (
            result.success &&
            result.attachment?.attachmentFile &&
            result.attachment?.mimeType
          ) {
            const fullFileUrl = attachmentService.getViewableFileUrl(
              result.attachment.attachmentFile,
              result.attachment.mimeType,
            );
            setThumbnailUrl(fullFileUrl);
          } else {
            setHasError(true);
          }
        } else {
          // For videos without thumbnail, show icon (will be handled by renderContent fallback)
          console.log(
            `ℹ️ No thumbnail available for video attachment ${attachment.id}, will show icon`,
          );
        }
      } catch (err) {
        console.error('Failed to load thumbnail:', err);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadThumbnail();
  }, [hasSource, isVisible, shouldShowThumbnail, attachment, isImage, isVideo]);

  /**
   * The picture to draw, or `null` for the type glyph.
   *
   * On the `source` path this is the caller's `uri` and nothing else: no
   * fetched url can exist, because no fetch ran (D1a). A `source` whose `uri`
   * is absent is the HEIC case — glyph, not spinner, not error dot, not an
   * empty box (Story 8.5, AC 4). A `uri` that FAILS to load (a revoked blob,
   * a bad data URI) falls back to the glyph the same way, via `onError`,
   * instead of leaving an empty box (#1069 MR !597 review).
   */
  const displayUri = source
    ? source.uri && source.uri !== failedSourceUri
      ? source.uri
      : null
    : shouldShowThumbnail && !hasError
    ? thumbnailUrl
    : null;

  const renderContent = () => {
    // Show loading spinner
    if (externalLoading || isLoading) {
      return (
        <View
          style={[
            styles.iconContainer,
            { width: sizeInDp, height: sizeInDp },
            containerStyle,
          ]}
        >
          <ActivityIndicator size="small" color="#1976d2" />
        </View>
      );
    }

    // Show thumbnail if available
    if (displayUri) {
      return (
        <View style={styles.thumbnailContainer}>
          <Image
            source={{ uri: displayUri }}
            // ⚠️ `containerStyle` REACHES THIS BRANCH TOO, AND THAT IS THE
            // POINT (Story 8.5, C3). `thumbnail` and `iconContainer` carry
            // the same `#f5f5f5`/`#e0e0e0` pair; dressing only one gives a
            // photo one edge and a document another inside one row list.
            style={[
              styles.thumbnail,
              { width: sizeInDp, height: sizeInDp },
              containerStyle,
            ]}
            resizeMode="cover"
            // Only the `source` form: the fetch form has its own error path
            // (`hasError`), and it is not this review's to change.
            onError={source ? () => setFailedSourceUri(displayUri) : undefined}
          />
          {isVideo && (
            <View style={styles.playOverlay}>
              <Play size={sizeInDp * 0.4} color="#fff" />
            </View>
          )}
          {hasError && <View style={styles.errorIndicator} />}
        </View>
      );
    }

    // Show file type icon as fallback
    return (
      <View
        style={[
          styles.iconContainer,
          { width: sizeInDp, height: sizeInDp },
          containerStyle,
        ]}
      >
        {getAttachmentIcon(mimeType, sizeInDp * 0.6)}
        {hasError && <View style={styles.errorIndicator} />}
      </View>
    );
  };

  const Wrapper = onPress ? TouchableOpacity : View;

  return (
    <Wrapper
      ref={containerRef as any}
      onPress={onPress}
      // ⚠️ THE ACCESSIBLE NAME COMES ONLY FROM THE `attachment` PATH (D1a).
      // A `source` caller owns the name: the correspondence row announces
      // `TYPE, name, size` and hides this subtree from the tree, so a second
      // name here would announce the same file twice (4.2 AC 14).
      accessible={attachment ? !!onPress : undefined}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={
        attachment ? `View ${attachment.attachmentName}` : undefined
      }
    >
      {renderContent()}
    </Wrapper>
  );
};

const styles = StyleSheet.create({
  thumbnailContainer: {
    position: 'relative',
  },
  thumbnail: {
    borderRadius: 4,
    borderWidth: 1,
    borderColor: ATTACHMENT_COLORS.CARD_BORDER_COLOR,
  },
  iconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: ATTACHMENT_COLORS.TABLE_HEADER_BACKGROUND_COLOR,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: ATTACHMENT_COLORS.CARD_BORDER_COLOR,
  },
  playOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: 4,
  },
  errorIndicator: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#d32f2f',
  },
});
